// Estilos do menu de opções de mensagem (M5.4a) da modernização do InfoChat.
//
// Função pura — sem JSX, sem I/O, sem leitura de localStorage/backend.
// Recebe o `theme` do MUI (idealmente já com `theme.infoChat` da Entrega
// M2) e devolve só objetos de estilo simples (CSS-in-JS), para serem
// mesclados nas classes já existentes de `MessageOptionsMenu`. Não aplica
// nada sozinha — quem decide onde/como usar é esse arquivo.
//
// Helper independente das demais entregas (M5.1/M5.2/M5.3a/M5.3b) — por
// decisão explícita, não importa nem se acopla a elas, mesmo definindo um
// `focusRingAccent` equivalente (duplicação intencional, cada entrega fica
// isolada).
//
// Escopo desta entrega (M5.4a): exclusivamente o anel de foco visível dos
// itens do menu de opções de mensagem (excluir, editar, responder,
// encaminhar, "falar com"). Nenhuma outra decisão visual — o menu não tinha
// nenhuma cor/estilo customizado antes desta entrega, e continua sem
// nenhum, além do anel de foco.
//
// Usa exclusivamente os tokens de `theme.infoChat` para cor — nunca um hex
// literal novo aqui.

import { createInfoChatDesignTokens } from "../theme/infoChatDesignTokens";

const hasCompleteInfoChatTokens = tokens =>
  Boolean(
    tokens &&
      tokens.brand &&
      tokens.surfaces &&
      tokens.borders &&
      tokens.text &&
      tokens.shadows &&
      tokens.transitions &&
      tokens.radii &&
      tokens.spacing &&
      tokens.status
  );

// Mesmo padrão de fallback seguro já usado nas entregas anteriores — nunca
// deixa o menu quebrar caso theme.infoChat esteja ausente/incompleto, e
// nunca diverge dos tokens reais por reaproveitar a mesma função pura da
// M2.
const resolveInfoChatTokens = theme => {
  if (hasCompleteInfoChatTokens(theme?.infoChat)) {
    return theme.infoChat;
  }
  return createInfoChatDesignTokens(theme?.palette?.type, theme?.palette?.primary?.main);
};

/**
 * Estilos do menu de opções de mensagem modernizado (M5.4a).
 *
 * @param {object} theme - tema do MUI (idealmente já com `theme.infoChat`).
 */
export const createInfoChatMessageOptionsMenuStyles = theme => {
  const tokens = resolveInfoChatTokens(theme);

  return {
    // Anel de foco visível (navegação real por teclado) — mesma cor
    // institucional, mesmo raciocínio já corrigido em M4/M5.1/M5.2/M5.3a/
    // M5.3b: usa `outline`/`outlineOffset`, nunca `boxShadow` ou
    // `backgroundColor`, para nunca disputar a mesma propriedade usada por
    // um eventual estado de hover/seleção — o foco sempre aparece por cima
    // de qualquer estado, sem sobrescrever nada. Aplicado diretamente ao
    // `MenuItem` real (o elemento que o MUI realmente marca como
    // `Mui-focusVisible` durante navegação por teclado em um `<Menu>`),
    // nunca a um filho decorativo.
    focusRingAccent: {
      outline: `2px solid ${tokens.brand.primaryColor}`,
      outlineOffset: "-2px"
    }
  };
};

export default createInfoChatMessageOptionsMenuStyles;
